import { Link } from "react-router-dom";
import {
  ArrowRight,
  CreditCard,
  Globe,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LANGS } from "@/i18n/translations";
import { useSettings } from "@/lib/hooks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PAYMENT_URL = "https://oim.cagrinetwork.com.tr/";

const FALLBACK = {
  company_fullname:
    "ÇAĞRINETWORK İNTERNET İLETİŞİM HABERLEŞME LTD ŞTİ",
  address: "Terzialiler, Cumhuriyet Cd. 145/A, 48600 Ortaca/Muğla",
  phone: "+90 850 303 5621",
  emails: ["info@cagrinetwork.com.tr", "admin@cagrinetwork.com.tr"],
};

const PARTICLES = Array.from({ length: 14 }, (_, i) => ({
  left: `${(i * 7 + 5) % 100}%`,
  top: `${(i * 13 + 20) % 100}%`,
  size: i % 3 === 0 ? 3 : 2,
  delay: `${(i % 7) * 1.3}s`,
  duration: `${7 + (i % 5) * 2}s`,
}));

const LONGITUDES = [0, 30, 60, 90, 120, 150];
const LATITUDES = [0, 45, 135];
const NODES = [
  { ry: 18, rx: 32, color: "#99F6E4", glow: "rgba(157,178,255,0.9)" },
  { ry: 112, rx: -22, color: "#5EEAD4", glow: "rgba(107,138,255,0.9)" },
  { ry: 205, rx: 55, color: "#0D9488", glow: "rgba(13,148,136,0.9)" },
  { ry: 300, rx: -48, color: "#99F6E4", glow: "rgba(157,178,255,0.9)" },
  { ry: 70, rx: -12, color: "#FFFFFF", glow: "rgba(220,230,255,0.9)" },
];

export default function ComingSoon() {
  const { t, lang, setLang } = useLang();
  const { data: settings } = useSettings();

  const fullname = settings?.company_fullname || FALLBACK.company_fullname;
  const address = settings?.address || FALLBACK.address;
  const phone = settings?.phone || FALLBACK.phone;
  const emails = settings?.emails?.length ? settings.emails : FALLBACK.emails;

  return (
    <section
      className="relative min-h-screen overflow-hidden bg-[#021A1A] text-white"
      data-testid="coming-soon-page"
    >
      {/* Ambient gradient orbs */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="ent-anim absolute -start-40 -top-40 h-[36rem] w-[36rem] rounded-full bg-[#0D9488]/30 blur-[120px]"
          style={{ animation: "ent-mesh-shift 13s ease-in-out infinite" }}
        />
        <div
          className="ent-anim absolute -end-40 top-1/3 h-[32rem] w-[32rem] rounded-full bg-[#5EEAD4]/20 blur-[120px]"
          style={{ animation: "ent-mesh-shift 16s ease-in-out infinite reverse" }}
        />
        <div
          className="ent-anim absolute -bottom-40 start-1/4 h-[30rem] w-[30rem] rounded-full bg-[#0D9488]/20 blur-[120px]"
          style={{ animation: "ent-mesh-shift 19s ease-in-out infinite" }}
        />
      </div>

      {/* Perspective grid floor */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] overflow-hidden"
        style={{ perspective: "600px" }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(13,148,136,0.22) 1px, transparent 1px), linear-gradient(to bottom, rgba(13,148,136,0.22) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            transform: "rotateX(74deg)",
            transformOrigin: "bottom",
            animation: "ent-grid-move 2.4s linear infinite",
            maskImage: "linear-gradient(to top, black, transparent 78%)",
            WebkitMaskImage: "linear-gradient(to top, black, transparent 78%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#021A1A] via-[#021A1A]/40 to-transparent" />
      </div>

      {/* Center 3D fiber network globe */}
      <div
        className="pointer-events-none absolute left-1/2 top-[46%] z-0 -translate-x-1/2 -translate-y-1/2"
        style={{ perspective: "1200px" }}
      >
        <div
          className="ent-anim relative h-[360px] w-[360px] max-w-[92vw] sm:h-[460px] sm:w-[460px]"
          style={{
            transformStyle: "preserve-3d",
            animation: "ent-globe-float 11s ease-in-out infinite",
          }}
        >
          {/* Spinning wireframe sphere */}
          <div
            className="ent-anim absolute inset-0"
            style={{
              transformStyle: "preserve-3d",
              animation: "ent-globe-spin 30s linear infinite",
            }}
          >
            {/* Longitudes (meridyenler) */}
            {LONGITUDES.map((a, i) => (
              <span
                key={`lon-${a}`}
                className="absolute inset-0 rounded-full"
                style={{
                  transform: `rotateY(${a}deg)`,
                  border: "1px solid",
                  borderColor:
                    i % 2 === 0 ? "rgba(13,148,136,0.45)" : "rgba(107,138,255,0.28)",
                }}
              />
            ))}
            {/* Latitudes (paraleller) */}
            {LATITUDES.map((a) => (
              <span
                key={`lat-${a}`}
                className="absolute inset-0 rounded-full"
                style={{
                  transform: `rotateX(${a}deg)`,
                  border: "1px solid rgba(157,178,255,0.22)",
                }}
              />
            ))}
            {/* Equator accent */}
            <span
              className="absolute inset-0 rounded-full"
              style={{
                transform: "rotateX(90deg)",
                border: "1px solid rgba(13,148,136,0.55)",
                boxShadow: "0 0 24px rgba(13,148,136,0.35)",
              }}
            />
            {/* Data nodes on orbits */}
            {NODES.map((n, i) => (
              <span
                key={`node-${i}`}
                className="absolute inset-0"
                style={{
                  transformStyle: "preserve-3d",
                  transform: `rotateY(${n.ry}deg) rotateX(${n.rx}deg)`,
                }}
              >
                <span
                  className="absolute left-1/2 top-0 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    background: n.color,
                    boxShadow: `0 0 16px 5px ${n.glow}`,
                  }}
                />
                <span
                  className="absolute left-1/2 top-0 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    background: n.color,
                    boxShadow: `0 0 8px 3px ${n.glow}`,
                    transform: "scale(1.4)",
                    opacity: 0.4,
                  }}
                />
              </span>
            ))}
          </div>

          {/* Outer faint ring (static) */}
          <span
            className="absolute -inset-6 rounded-full"
            style={{ border: "1px solid rgba(255,255,255,0.06)" }}
          />

          {/* Central halo glow */}
          <div
            className="ent-anim absolute left-1/2 top-1/2 h-40 w-40 rounded-full sm:h-52 sm:w-52"
            style={{
              background:
                "radial-gradient(circle at 38% 32%, rgba(157,178,255,0.9) 0%, rgba(13,148,136,0.55) 42%, rgba(0,20,100,0) 72%)",
              animation: "ent-glow-pulse 5s ease-in-out infinite",
            }}
          />
        </div>
      </div>

      {/* Radial light beams from center */}
      <div className="pointer-events-none absolute left-1/2 top-[46%] z-0 -translate-x-1/2 -translate-y-1/2">
        {[0, 45, 90, 135].map((deg) => (
          <span
            key={deg}
            className="ent-anim absolute left-0 top-0 h-px w-[520px] origin-left"
            style={{
              transform: `rotate(${deg}deg)`,
              background:
                "linear-gradient(to right, rgba(13,148,136,0.5), transparent)",
              animation: `ent-beam ${6 + (deg % 3)}s ease-in-out ${deg / 60}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Floating particles */}
      <div className="pointer-events-none absolute inset-0">
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className="ent-anim absolute rounded-full bg-[#5EEAD4]/60"
            style={{
              left: p.left,
              top: p.top,
              width: p.size,
              height: p.size,
              animation: `ent-particle-drift ${p.duration} ease-in ${p.delay} infinite`,
            }}
          />
        ))}
      </div>

      {/* Top bar */}
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex items-center shrink-0">
          <img src="/cagrilogo.png" alt="ÇağrıNET" className="h-7 w-auto brightness-0 invert" />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="coming-soon-language-switcher"
            className="flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm font-bold text-white backdrop-blur-md transition-colors hover:border-[#0D9488] hover:text-[#99F6E4]"
          >
            <Globe className="h-4 w-4" />
            {LANGS.find((l) => l.code === lang)?.short}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {LANGS.map((l) => (
              <DropdownMenuItem
                key={l.code}
                data-testid={`coming-soon-language-option-${l.code}`}
                onClick={() => setLang(l.code)}
                className={lang === l.code ? "bg-zinc-100 font-bold" : ""}
              >
                {l.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Center content */}
      <main className="relative z-10 flex min-h-[calc(100vh-7rem)] items-center justify-center px-4 pb-16">
        <div className="w-full max-w-2xl">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 text-center shadow-[0_40px_120px_-30px_rgba(0,0,0,0.8)] backdrop-blur-2xl sm:p-12">
            {/* inner top accent */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#0D9488] to-transparent" />

            <span
              className="animate-fade-in inline-flex items-center gap-2 rounded-full border border-[#0D9488]/40 bg-[#0D9488]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-[#99F6E4]"
              data-testid="coming-soon-badge"
            >
              <span className="relative flex h-2 w-2">
                <span className="ent-anim absolute inline-flex h-full w-full rounded-full bg-[#0D9488] opacity-75" style={{ animation: "ent-btn-ring 1.8s ease-out infinite" }} />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#0D9488]" />
              </span>
              {t("comingSoon.badge")}
            </span>

            <h1
              className="animate-fade-up mt-6 font-heading text-4xl font-black leading-[1.05] tracking-tighter text-white delay-100 sm:text-5xl md:text-6xl"
              data-testid="coming-soon-title"
            >
              <span className="block">{t("comingSoon.title1")}</span>
              <span className="mt-1 block bg-gradient-to-r from-[#99F6E4] via-[#0D9488] to-[#5EEAD4] bg-clip-text text-transparent">
                {t("comingSoon.title2")}
              </span>
            </h1>

            <p className="animate-fade-up mx-auto mt-5 max-w-xl text-sm leading-relaxed text-zinc-300 delay-200 sm:text-base">
              {t("comingSoon.subtitle")}
            </p>

            {/* Payment button */}
            <div className="animate-fade-up mt-9 flex justify-center delay-300">
              <a
                href={PAYMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="coming-soon-payment-button"
                className="group relative inline-flex items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-r from-[#0D9488] to-[#0F766E] px-7 py-4 text-sm font-bold text-white shadow-[0_18px_50px_-12px_rgba(13,148,136,0.85)] ring-1 ring-white/25 transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_26px_70px_-12px_rgba(13,148,136,0.95)] sm:px-9 sm:text-base"
              >
                <span
                  className="ent-anim pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-[#0D9488]"
                  style={{ animation: "ent-btn-ring 2.2s ease-out infinite" }}
                />
                <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
                  <CreditCard className="h-4 w-4" />
                </span>
                <span className="relative">{t("comingSoon.paymentBtn")}</span>
                <ArrowRight className="relative h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </a>
            </div>
            <p className="animate-fade-in mt-3 text-xs text-zinc-400 delay-300">
              {t("comingSoon.paymentNote")}
            </p>

            {/* Contact */}
            <div className="mt-10 border-t border-white/10 pt-7">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                {t("comingSoon.contactTitle")}
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-4 text-start sm:grid-cols-3">
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.03] p-3 transition-colors hover:border-[#0D9488]/40"
                  data-testid="coming-soon-address"
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#0D9488]" />
                  <span className="text-xs leading-relaxed text-zinc-300">{address}</span>
                </a>
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  className="group flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.03] p-3 transition-colors hover:border-[#0D9488]/40"
                  data-testid="coming-soon-phone"
                >
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#0D9488]" />
                  <span className="text-xs leading-relaxed text-zinc-300">{phone}</span>
                </a>
                <div className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.03] p-3">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#0D9488]" />
                  <div className="flex flex-col gap-1">
                    {emails.map((email) => (
                      <a
                        key={email}
                        href={`mailto:${email}`}
                        className="text-xs leading-relaxed text-zinc-300 transition-colors hover:text-white"
                      >
                        {email}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Company identity */}
          <p className="animate-fade-in mx-auto mt-8 max-w-xl text-center text-xs leading-relaxed text-zinc-500">
            {fullname}
          </p>
          <p className="mt-2 text-center text-[11px] font-medium text-zinc-600">
            © {new Date().getFullYear()} ÇağrıNET ·{" "}
            <Link to="/admin/login" className="transition-colors hover:text-zinc-400" data-testid="coming-soon-admin-link">
              Admin
            </Link>
          </p>
        </div>
      </main>
    </section>
  );
}
